<header class="flex header p-2">
  <h2>{{ 'batchDyeingCycle.title' | translateWF: 'Dyeing Cycle' }} — {{ 'batchDyeingCycle.batch' | translateWF: 'Batch' }} {{ batchNumber }}</h2>
</header>

<section class="content">
  <p-confirmDialog />
  @if (isLoading() > 0) {
    <loading-overlay [isLoading]="true" />
  }

  @if (activeCycle()) {
    <div class="cycle-info-bar">
      <div class="cycle-info-row">
        <span class="info-label">{{ 'batchDyeingCycle.cycle' | translateWF: 'Cycle' }}</span>
        <span class="info-value">#{{ activeCycle()!.cycleNumber }} — {{ activeCycle()!.number }}</span>
      </div>
      <div class="cycle-info-row">
        <span class="info-label">{{ 'Station' | translateWF: 'Station' }}</span>
        <span class="info-value">{{ activeCycle()!.startStationAssetId ?? stationAssetId }}</span>
      </div>
      <div class="cycle-info-row">
        <span class="info-label">{{ 'Start' | translateWF: 'Start' }}</span>
        <span class="info-value">{{ activeCycle()!.startTime | date: 'short' }}</span>
      </div>
      @if (activeCycle()!.endTime) {
        <div class="cycle-info-row">
          <span class="info-label">{{ 'End' | translateWF: 'End' }}</span>
          <span class="info-value">{{ activeCycle()!.endTime | date: 'short' }}</span>
        </div>
      }
    </div>
  }

  @if (cyclePhase() === 'start') {
    <div class="action-section">
      <p class="text-sm text-gray-500 mb-3">
        {{ 'batchDyeingCycle.startHint' | translateWF: 'Start a new dyeing cycle on station' }} <strong>{{ stationAssetId }}</strong>
      </p>
      <p-button [label]="'batchDyeingCycle.startCycle' | translateWF: 'Start Cycle'" severity="success" icon="pi pi-play" (onClick)="confirmStartCycle()" />
    </div>
  }

  @if (cyclePhase() === 'running') {
    <div class="action-section">
      <div class="flex gap-2">
        <p-button [label]="'batchDyeingCycle.endCycle' | translateWF: 'End Cycle'" severity="warn" icon="pi pi-stop-circle" (onClick)="confirmEndCycle()" />
        @if (showHardStopButton()) {
          <p-button [label]="'batchDyeingCycle.hardStop' | translateWF: 'Hard Stop'" severity="danger" icon="pi pi-ban" (onClick)="confirmHardStop()" />
        }
      </div>
    </div>
  }

  @if (showQASection()) {
    <div class="action-section">
      <h4 class="font-semibold mb-3">{{ 'batchDyeingCycle.qualityControl' | translateWF: 'Quality Control' }}</h4>
      <div class="flex flex-col gap-3 w-full">
        <div class="flex items-center gap-3">
          <label class="font-medium text-sm">{{ 'Status' | translateWF: 'Status' }}:</label>
          <p-selectButton
            [options]="[
              { label: 'Approve' | translateWF: 'Approve', value: true },
              { label: 'Reject' | translateWF: 'Reject', value: false },
            ]"
            [formControl]="isApprovedControl"
            optionLabel="label"
            optionValue="value"
          />
        </div>

        @if (showCommentsField()) {
          <div class="flex flex-col gap-1">
            <label for="qaComments" class="font-medium text-sm"> {{ 'batchDyeingCycle.commentsRequiredLabel' | translateWF: 'Comments (Required)' }}: </label>
            <textarea
              id="qaComments"
              pInputTextarea
              rows="2"
              class="w-full"
              [placeholder]="'batchDyeingCycle.commentsPlaceholder' | translateWF: 'Enter rejection comments'"
              [formControl]="qaComments"
            ></textarea>
          </div>
        }

        <p-button [label]="'batchDyeingCycle.submitQA' | translateWF: 'Submit Quality Control'" severity="primary" icon="pi pi-check" (onClick)="confirmSubmitQA()" />
      </div>
    </div>
  }

  @if (cyclePhase() === 'unloading') {
    <div class="action-section">
      @if (activeCycle()?.colorStatusApproval; as approval) {
        <div class="mb-3">
          <p-tag
            [value]="approval.isApproved ? ('batchDyeingCycle.colorApproved' | translateWF: 'Color Approved') : ('batchDyeingCycle.colorRejected' | translateWF: 'Color Rejected')"
            [severity]="approval.isApproved ? 'success' : 'danger'"
          />
          @if (!approval.isApproved && approval.comments) {
            <span class="text-sm text-red-600 ml-2">{{ approval.comments }}</span>
          }
        </div>
      }
      <div class="flex gap-2">
        <p-button [label]="'batchDyeingCycle.unloadMachine' | translateWF: 'Unload Machine'" severity="info" icon="pi pi-download" (onClick)="confirmUnload()" />
        @if (showHardApproveButton()) {
          <p-button [label]="'batchDyeingCycle.hardApprove' | translateWF: 'Hard Approve'" severity="danger" icon="pi pi-shield" (onClick)="confirmHardApprove()" />
        }
      </div>
    </div>
  }

  @if (cyclePhase() === 'done' && isConsumptionsFinalized()) {
    <div class="action-section">
      <p-tag severity="success">
        <i class="pi pi-check-circle mr-1"></i>
        {{ 'batchDyeingCycle.cycleComplete' | translateWF: 'Cycle Complete' }}
      </p-tag>
    </div>
  }

  @if (showConsumptionsSection()) {
    <div class="consumptions-section">
      <h4 class="font-semibold mb-2">{{ 'batchDyeingCycle.itemConsumptions' | translateWF: 'Item Consumptions' }}</h4>
      <p class="text-xs text-gray-500 mb-3">
        {{ 'batchDyeingCycle.consumptionsHint' | translateWF: 'Enter the quantities actually consumed. You can add items that were not in the recipe.' }}
      </p>

      <div class="flex items-center gap-3 mb-3">
        <label for="waterUsage" class="font-medium text-sm">{{ 'batchDyeingCycle.waterUsage' | translateWF: 'Water Usage (L)' }}:</label>
        <div class="w-48">
          <p-inputgroup>
            <p-inputNumber inputId="waterUsage" [formControl]="waterUsageControl" [min]="0" mode="decimal" locale="en-US" [minFractionDigits]="0" [maxFractionDigits]="2" fluid />
            <p-inputgroup-addon>L</p-inputgroup-addon>
          </p-inputgroup>
        </div>
        @if (waterUsageVariance(); as waterVariance) {
          <span class="text-sm text-gray-500">{{ 'Estimated' | translateWF: 'Estimated' }}: {{ waterVariance.estimated | number: '1.0-2' }} L</span>
          <app-consumption-variance class="text-sm" [variance]="waterVariance" unit="L" />
        }
      </div>

      <div class="flex items-center gap-2 mb-3">
        <p-select
          [options]="selectableItems()"
          [formControl]="itemToAddControl"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label,value"
          [showClear]="true"
          [placeholder]="'batchDyeingCycle.selectItemToAdd' | translateWF: 'Select an item to add'"
          [emptyMessage]="'batchDyeingCycle.noItemsToAdd' | translateWF: 'No more items available on this batch'"
          styleClass="w-72"
        />
        <p-button
          [label]="'batchDyeingCycle.addItem' | translateWF: 'Add item'"
          icon="pi pi-plus"
          variant="outlined"
          [disabled]="!itemToAddControl.value"
          (onClick)="addConsumptionRow()"
        />
      </div>

      @if (consumptionItemsForm.length === 0) {
        <p class="text-sm text-gray-400">{{ 'batchDyeingCycle.noIngredients' | translateWF: 'No recipe ingredients available.' }}</p>
      } @else {
        <p-table [value]="consumptionItemsForm.controls" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'Item' | translateWF: 'Item' }}</th>
              @if (hasEstimate()) {
                <th class="text-right" style="width: 110px">{{ 'Estimated' | translateWF: 'Estimated' }}</th>
              }
              <th style="width: 140px">{{ 'Actual' | translateWF: 'Actual' }}</th>
              <th style="width: 70px">{{ 'UOM' | translateWF: 'UOM' }}</th>
              @if (hasEstimate()) {
                <th style="width: 170px">{{ 'Difference' | translateWF: 'Difference' }}</th>
              }
              <th style="width: 60px"></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-group let-rowIndex="rowIndex">
            @let row = consumptionRowViews()[rowIndex];
            <tr [formGroup]="group">
              <td>
                <div class="font-medium">{{ group.controls.itemCode.value }}</div>
                @if (row.displayName) {
                  <div class="text-xs text-gray-500">{{ row.displayName }}</div>
                }
                @if (!group.controls.isFromRecipe.value) {
                  <div class="text-xs text-blue-600 flex items-center gap-1 mt-1">
                    <i class="pi pi-plus-circle"></i>
                    <span>{{ 'batchDyeingCycle.outsideRecipe' | translateWF: 'Added outside the recipe.' }}</span>
                  </div>
                }
                @if (!group.controls.isAvailable.value) {
                  <div class="text-xs text-orange-600 flex items-center gap-1 mt-1">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span>{{ 'batchDyeingCycle.noLotItems' | translateWF: 'No lot items available for this item.' }}</span>
                  </div>
                }
                @if (row.recipeUomHint) {
                  <div class="text-xs text-orange-600 flex items-center gap-1 mt-1">
                    <i class="pi pi-info-circle"></i>
                    <span>{{ row.recipeUomHint }}</span>
                  </div>
                }
              </td>
              @if (hasEstimate()) {
                <td class="text-right text-gray-500">
                  @if (row.variance; as variance) {
                    {{ variance.estimated | number: '1.0-2' }}
                  } @else {
                    —
                  }
                </td>
              }
              <td>
                <p-inputNumber
                  formControlName="quantity"
                  [min]="0"
                  mode="decimal"
                  locale="en-US"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="2"
                  styleClass="w-full"
                  inputStyleClass="w-full"
                />
              </td>
              <td class="text-gray-500">{{ row.uomLabel }}</td>
              @if (hasEstimate()) {
                <td><app-consumption-variance [variance]="row.variance" /></td>
              }
              <td>
                @if (!group.controls.isFromRecipe.value) {
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    [text]="true"
                    size="small"
                    [pTooltip]="'batchDyeingCycle.removeItem' | translateWF: 'Remove item'"
                    tooltipPosition="top"
                    (onClick)="removeConsumptionRow(rowIndex)"
                  />
                }
              </td>
            </tr>
          </ng-template>
        </p-table>

        <div class="flex justify-end gap-2 mt-3">
          <p-button
            [label]="'batchDyeingCycle.saveConsumptions' | translateWF: 'Save Consumptions'"
            icon="pi pi-save"
            variant="outlined"
            (onClick)="saveConsumptions()"
            [loading]="isSavingConsumptions()"
          />
          <p-button
            [label]="'batchDyeingCycle.consume' | translateWF: 'Consume'"
            icon="pi pi-check-circle"
            severity="success"
            (onClick)="confirmConsume()"
            [loading]="isConsuming()"
          />
        </div>
      }
    </div>
  }

  @if (isConsumptionsFinalized() && activeCycle()?.itemConsumptions?.length) {
    <div class="consumptions-section">
      <div class="flex items-center gap-2 mb-2">
        <h4 class="font-semibold">{{ 'batchDyeingCycle.itemConsumptions' | translateWF: 'Item Consumptions' }}</h4>
        <p-tag [value]="'Finalized' | translateWF: 'Finalized'" severity="success" />
      </div>
      @if (activeCycle()!.waterUsageInLiters || finalizedWaterUsageVariance()) {
        <p class="text-sm mb-2 flex items-center gap-2">
          <span>
            {{ 'batchDyeingCycle.waterUsageShort' | translateWF: 'Water Usage' }}:
            <strong>{{ activeCycle()!.waterUsageInLiters ?? 0 | number: '1.0-2' }} L</strong>
          </span>
          @if (finalizedWaterUsageVariance(); as waterVariance) {
            <span class="text-gray-500">{{ 'Estimated' | translateWF: 'Estimated' }}: {{ waterVariance.estimated | number: '1.0-2' }} L</span>
            <app-consumption-variance [variance]="waterVariance" unit="L" />
          }
        </p>
      }
      <p-table [value]="finalizedConsumptions()" styleClass="p-datatable-sm">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ 'Item' | translateWF: 'Item' }}</th>
            @if (hasEstimate()) {
              <th class="text-right">{{ 'Estimated' | translateWF: 'Estimated' }}</th>
            }
            <th class="text-right">{{ 'Actual' | translateWF: 'Actual' }}</th>
            <th>{{ 'UOM' | translateWF: 'UOM' }}</th>
            @if (hasEstimate()) {
              <th>{{ 'Difference' | translateWF: 'Difference' }}</th>
            }
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
          <tr>
            <td>
              <div class="font-medium">{{ item.itemCode }}</div>
              @if (item.displayName) {
                <div class="text-xs text-gray-500">{{ item.displayName }}</div>
              }
            </td>
            @if (hasEstimate()) {
              <td class="text-right text-gray-500">
                @if (item.variance; as variance) {
                  {{ variance.estimated | number: '1.0-2' }}
                } @else {
                  —
                }
              </td>
            }
            <td class="text-right">{{ item.quantity | number: '1.0-2' }}</td>
            <td class="text-gray-500">{{ item.uomLabel }}</td>
            @if (hasEstimate()) {
              <td><app-consumption-variance [variance]="item.variance" /></td>
            }
          </tr>
        </ng-template>
      </p-table>
    </div>
  }
</section>

<footer>
  <p-button [label]="'Close' | translateWF: 'Close'" variant="outlined" (onClick)="onClose()" />
</footer>
